import {
    Component,
    ojs,
    app,
    state,
    v,
    each,
    payload,
} from "modular-openscriptjs";
import { dom, tool } from "../../../fotastart";
import {
    monetary,
    oneOf,
    pageInfo,
    formatDateForHumans,
    formatDate,
    lastOf,
    cleanDispose,
    either,
    leaveEchoChannel,
    eParse,
} from "../../../helpers";
import {
    trackCompletedOrderForReview,
    maybeTriggerAppReviewPrompt,
} from "../../../reviewPrompt";
import { checkPendingBusinessRatings } from "../../../businessRatingPrompt";
import { dc, PAGES, api } from "../../../declarations";
import OrderStatus from "../../Blocks/OrderStatus";
import PaymentStatus from "../../Blocks/PaymentStatus";
import QRCodeModal from "../../Modals/QRCodeModal";
import ProfilePic from "../../ProfilePic";
import OrderDetailProductView from "../../Views/OrderDetailProductView";
import OrderDetailDeliveryRequestView from "../../Views/OrderDetailDeliveryRequestView";
import CancelOrderForm from "../../Forms/CancelOrderForm";
import OrderedItems from "../../Blocks/OrderedItems";
import { Capacitor } from "@capacitor/core";
import { getFirebaseToken } from "../../../firebase";

const h = app("h");
const broker = app("broker");
const route = app("router");
const $e = app("$e");
const gc = app("gc");
const uc = app("uc");

// Give the deliverer/delivery review prompt (which fires ~2s after an
// order completes, see below) room to be seen and used first, rather
// than stacking a second modal on top of it the instant the order
// completes. The customer also needs a moment to actually have the
// product in hand before we ask how it was.
const BUSINESS_RATING_PROMPT_DELAY_MS = 60 * 1000;

ojs(
    OrderStatus,
    PaymentStatus,
    QRCodeModal,
    ProfilePic,
    OrderDetailProductView,
    OrderDetailDeliveryRequestView,
    CancelOrderForm,
    OrderedItems,
);

export default class TrackOrder extends Component {
    review = state(null);
    orderTracked = state(null);
    timeoutId = null;
    reviewTimeoutId = null;
    businessRatingTimeoutId = null;
    imagesModal = null;

    async fetchOrder() {
        let orderId = route.params.orderId;
        if (!orderId) return;

        let res = await api.get(`/orders/${orderId}`);
        if (res && res.data && res.data.order) {
            this.orderTracked.value = res.data.order;
        } else {
            route.to("/404");
        }
    }

    $_mounted() {
        if (Capacitor.isNativePlatform()) {
            getFirebaseToken();
        }
    }

    render(...args) {
        pageInfo(PAGES.ORDER_DETAILS);

        this.fetchOrder();

        return h.div(
            { class: "container pt-3 pb-5 mb-5 py-md-5" },
            v(this.orderTracked, (orderState) => {
                let val = orderState.value;
                if (!val) {
                    return h.div(
                        {
                            class: "d-flex justify-content-center align-items-center py-5",
                        },
                        h.div({
                            class: "spinner-border text-primary",
                            role: "status",
                        }),
                    );
                }

                let address = val?.delivery_address;

                let deliverer = oneOf(val?.current_deliverer, {
                    first_name: "---",
                    last_name: "---",
                    phone: "---",
                    email: "---",
                    profile_photo_path: null,
                    display_name: "Deliverer",
                });

                clearInterval(this.timeoutId);
                clearTimeout(this.reviewTimeoutId);
                clearTimeout(this.businessRatingTimeoutId);

                if (
                    val?.id &&
                    val?.id == route.params.orderId &&
                    val?.status in { completed: true }
                ) {
                    // Count this towards the "rate the app" nudge regardless
                    // of whether the customer leaves a review or not.
                    trackCompletedOrderForReview(val.id);

                    // Also check whether there are any businesses from
                    // this (or another) recently completed order still
                    // waiting to be rated - delayed so it doesn't pop up
                    // on top of the deliverer review prompt below.
                    this.businessRatingTimeoutId = setTimeout(() => {
                        checkPendingBusinessRatings();
                    }, BUSINESS_RATING_PROMPT_DELAY_MS);

                    if (val?.review == null) {
                        this.reviewTimeoutId = setTimeout(() => {
                            this.editReview(val?.id, val?.review);
                        }, 2000);
                    } else {
                        // Already reviewed this order, so the review form
                        // won't pop up - check if it's time to nudge them
                        // to rate the app instead.
                        this.reviewTimeoutId = setTimeout(() => {
                            maybeTriggerAppReviewPrompt();
                        }, 2000);
                    }
                }

                if (Array.isArray(deliverer)) deliverer = deliverer.pop();

                this.review.value = val?.review;

                return h.div(
                    {
                        class: "col-lg-9 col-xl-7 d-flex flex-column gap-4 py-md-5",
                    },

                    // Pay Now Sticky Header
                    either(val?.status === "pending", () =>
                        h.div(
                            {
                                class: "sticky-top bg-white shadow-sm rounded-4 p-4 d-flex flex-column gap-3 border border-primary-subtle",
                                style: "top: 60px; z-index: 1020;",
                            },
                            h.div(
                                {
                                    class: "d-flex align-items-center gap-3 text-primary",
                                },
                                h.i({
                                    class: "bi bi-credit-card-2-front fs-3",
                                }),
                                h.span(
                                    { class: "fs-5 fw-bold" },
                                    "Action Required",
                                ),
                            ),
                            h.p(
                                { class: "m-0 text-muted" },
                                "Please complete your payment to confirm this order.",
                            ),

                            h.button(
                                {
                                    class: "btn btn-primary w-100 rounded-pill py-3 fw-bold shadow-sm d-flex justify-content-between align-items-center px-4",
                                    onclick: this.method("getPayment", val),
                                },
                                h.span("Pay Now"),
                                h.i({ class: "fa-solid fa-chevron-right" }),
                            ),
                        ),
                    ),

                    h.OrderStatus(val?.status, val?.type),

                    // Order Information Card
                    h.div(
                        { class: "card border-0 shadow-sm rounded-4" },
                        h.div(
                            { class: "card-body p-4" },
                            h.h5(
                                { class: "fw-bold text-secondary mb-4" },
                                "Order Information",
                            ),
                            h.div(
                                { class: "d-flex flex-column gap-3" },
                                this.infoRow(
                                    "Order Code",
                                    val?.order_code ?? "",
                                    "bi bi-upc-scan",
                                ),
                                this.infoRow(
                                    "Placed",
                                    either(
                                        val?.created_at,
                                        formatDateForHumans(val?.created_at),
                                    ),
                                    "bi bi-calendar3",
                                ),
                                either(val?.should_place_at, () =>
                                    this.infoRow(
                                        "Scheduled For",
                                        formatDateForHumans(
                                            val.should_place_at,
                                        ),
                                        "bi bi-clock-history text-primary",
                                    ),
                                ),
                                this.infoRow(
                                    "You Paid",
                                    monetary(
                                        val?.total_paid ?? 0,
                                        dc.userCurrency,
                                    ),
                                    "bi bi-wallet2",
                                ),

                                either(
                                    val?.unpaid,
                                    this.infoRow(
                                        "Unpaid",
                                        monetary(
                                            val?.unpaid ?? 0,
                                            dc.userCurrency,
                                        ),
                                        "bi bi-exclamation-circle text-danger",
                                    ),
                                ),

                                this.infoRow(
                                    "Delivery Fee",
                                    monetary(
                                        val?.total_delivery_price ?? 0,
                                        dc.userCurrency,
                                    ),
                                    "bi bi-truck",
                                ),

                                either(val?.delivery_tokens, () =>
                                    this.infoRow(
                                        "Prepaid Delivery",
                                        `${val?.delivery_tokens} vKM`,
                                        "bi bi-coin",
                                    ),
                                ),

                                either(
                                    val?.created_at,
                                    this.infoRow(
                                        "Est. Arrival",
                                        formatDateForHumans(val?.delivered_at),
                                        "bi bi-clock-history",
                                    ),
                                ),
                            ),
                        ),
                    ),

                    // Overpayment (Surpluses)
                    either(
                        val?.surpluses && val?.surpluses?.length > 0,
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4" },
                            h.div(
                                {
                                    class: "card-header bg-transparent border-0 pt-4 px-4 pb-2 d-flex justify-content-between align-items-center",
                                },
                                h.h5(
                                    { class: "fw-bold text-secondary mb-0" },
                                    "Overpayment",
                                ),
                                either(
                                    uc?.settings?.value?.auto_reimbursement
                                        ?.value == 1,
                                    h.small(
                                        { class: "text-muted fst-italic" },
                                        "Auto-refund enabled",
                                    ),
                                    h.a(
                                        {
                                            href: "#",
                                            class: "small text-decoration-none",
                                            onclick: `route.to('/settings')`,
                                        },
                                        h.i({ class: "fas fa-gear me-1" }),
                                        "Enable auto-refund",
                                    ),
                                ),
                            ),
                            h.div(
                                { class: "card-body p-4 pt-0" },
                                each(val?.surpluses, (surplus) =>
                                    h.div(
                                        {
                                            class: "bg-light rounded-3 p-3 d-flex flex-column gap-3 border border-light-subtle mb-3",
                                        },
                                        h.div(
                                            {
                                                class: "d-flex justify-content-between align-items-center",
                                            },
                                            h.span(
                                                { class: "fw-bold text-dark" },
                                                either(
                                                    surplus.type == "positive",
                                                    "Refund Due",
                                                    "You Owe",
                                                ),
                                            ),
                                            h.h5(
                                                {
                                                    class: "mb-0 fw-bold text-success",
                                                },
                                                monetary(
                                                    surplus.amount,
                                                    dc.userCurrency,
                                                ),
                                            ),
                                        ),
                                        h.div(
                                            {
                                                class: "d-flex gap-3 align-items-center text-muted small",
                                            },
                                            h.PaymentStatus(
                                                surplus.reimbursed == 1
                                                    ? "completed"
                                                    : "pending",
                                            ),
                                            h.span("•"),
                                            h.span(
                                                formatDateForHumans(
                                                    surplus.created_at,
                                                ),
                                            ),
                                        ),
                                        this.getSurplusButtons(surplus),
                                    ),
                                ),
                            ),
                        ),
                    ),

                    // Review Section
                    either(val?.status in { completed: true }, () => {
                        return v(this.review, (rev) => {
                            let review = rev.value;

                            return either(
                                review,
                                () => {
                                    // Existing Review View
                                    return h.div(
                                        {
                                            class: "card border-0 shadow-sm rounded-4",
                                        },
                                        h.div(
                                            {
                                                class: "card-header bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center",
                                            },
                                            h.h5(
                                                {
                                                    class: "fw-bold text-secondary mb-0",
                                                },
                                                "Your Review",
                                            ),
                                            h.button(
                                                {
                                                    class: "btn btn-sm btn-outline-secondary rounded-pill px-3",
                                                    onclick: this.method(
                                                        "editReview",
                                                        [val?.id, review],
                                                    ),
                                                },
                                                h.i({
                                                    class: "fa-solid fa-pen me-2",
                                                }),
                                                "Edit",
                                            ),
                                        ),
                                        h.div(
                                            { class: "card-body p-4" },
                                            h.h6(
                                                { class: "fw-bold mb-2" },
                                                review?.title,
                                            ),
                                            h.div(
                                                {
                                                    class: "d-flex align-items-center gap-2 mb-3",
                                                },
                                                h.div(
                                                    {
                                                        class: "d-flex gap-1 text-warning fs-5",
                                                    },
                                                    each([1, 2, 3, 4, 5], (r) =>
                                                        h.i({
                                                            class:
                                                                r <=
                                                                review?.rating
                                                                    ? "fas fa-star"
                                                                    : "far fa-star",
                                                        }),
                                                    ),
                                                ),
                                                h.span(
                                                    {
                                                        class: "badge bg-warning-subtle text-warning-emphasis fw-bold rounded-pill px-2.5 py-1 small",
                                                    },
                                                    `${review?.rating}/5`,
                                                ),
                                            ),
                                            h.p(
                                                { class: "text-muted mb-3" },
                                                review?.review,
                                            ),
                                            h.small(
                                                { class: "text-secondary" },
                                                formatDateForHumans(
                                                    review?.updated_at,
                                                ),
                                            ),
                                        ),
                                    );
                                },
                                () => {
                                    // Create Review Call to Action
                                    return h.div(
                                        {
                                            class: "card border-0 shadow-sm rounded-4 bg-primary-subtle",
                                        },
                                        h.div(
                                            {
                                                class: "card-body p-5 text-center",
                                            },
                                            h.div(
                                                {
                                                    class: "bg-white circle d-inline-flex p-3 mb-3 shadow-sm",
                                                },
                                                h.i({
                                                    class: "fas fa-star fs-2 text-warning",
                                                }),
                                            ),
                                            h.h4(
                                                { class: "fw-bold mb-3" },
                                                "How did we do?",
                                            ),
                                            h.button(
                                                {
                                                    class: "btn btn-primary rounded-pill px-4 py-2 fw-bold shadow-sm",
                                                    onclick: this.method(
                                                        "editReview",
                                                        [val?.id, review],
                                                    ),
                                                },
                                                "Rate this Delivery",
                                            ),
                                        ),
                                    );
                                },
                            );
                        });
                    }),

                    // Delivery Unit
                    either(
                        val?.status && val?.status !== "pending",
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4" },
                            h.div(
                                { class: "card-body p-4" },
                                h.h5(
                                    { class: "fw-bold text-secondary mb-4" },
                                    "Delivery Unit",
                                ),

                                either(
                                    val?.fulfilment_unit || val?.fulfilmentUnit,
                                    () => {
                                        let unit =
                                            val?.fulfilment_unit ||
                                            val?.fulfilmentUnit;
                                        let pc =
                                            unit?.purchase_coordinator ||
                                            unit?.purchaseCoordinator;
                                        let del =
                                            unit?.deliverer ||
                                            val?.current_deliverer;
                                        if (Array.isArray(del)) del = del[0];

                                        let elements = [];

                                        // Horizontal banner: the fulfilment
                                        // unit's own name.
                                        if (unit?.name) {
                                            elements.push(
                                                h.div(
                                                    {
                                                        class: "d-flex align-items-center gap-3 mb-4",
                                                    },
                                                    h.span(
                                                        {
                                                            class: "d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0",
                                                            style: "width: 40px; height: 40px;",
                                                        },
                                                        h.i({
                                                            class: "fas fa-truck",
                                                        }),
                                                    ),
                                                    h.div(
                                                        { class: "overflow-hidden" },
                                                        h.div(
                                                            {
                                                                class: "text-secondary text-uppercase fw-bold",
                                                                style: "font-size: 0.65rem; letter-spacing: 0.5px;",
                                                            },
                                                            "Assigned Team",
                                                        ),
                                                        h.div(
                                                            {
                                                                class: "fw-bold text-dark text-truncate",
                                                            },
                                                            unit.name,
                                                        ),
                                                    ),
                                                ),
                                            );
                                        }

                                        // Two columns below the banner: the
                                        // Purchase Coordinator and the
                                        // Logistics Associate (deliverer).
                                        // If only one of them is assigned,
                                        // it gets the full width instead of
                                        // sitting alone in a half column.
                                        let personColClass =
                                            pc && del ? "col-6" : "col-12";
                                        let personColumns = [];

                                        if (pc) {
                                            personColumns.push(
                                                h.div(
                                                    { class: personColClass },
                                                    this.renderUnitPersonCard(
                                                        pc,
                                                        "Purchase Coordinator",
                                                        val,
                                                    ),
                                                ),
                                            );
                                        }

                                        if (del) {
                                            personColumns.push(
                                                h.div(
                                                    { class: personColClass },
                                                    this.renderUnitPersonCard(
                                                        del,
                                                        "Logistics Associate",
                                                        val,
                                                    ),
                                                ),
                                            );
                                        }

                                        if (personColumns.length > 0) {
                                            elements.push(
                                                h.div(
                                                    { class: "row g-3" },
                                                    ...personColumns,
                                                ),
                                            );
                                        }

                                        if (elements.length === 0) {
                                            return h.div(
                                                {
                                                    class: "py-3 text-muted d-flex align-items-center gap-3",
                                                },
                                                h.i({
                                                    class: "fas fa-truck fs-3 text-secondary animate-pulse",
                                                }),
                                                h.p(
                                                    {
                                                        class: "m-0 small fw-medium text-start",
                                                    },
                                                    "We are assigning a delivery unit to process your order shortly.",
                                                ),
                                            );
                                        }

                                        return h.div(
                                            { class: "d-flex flex-column" },
                                            ...elements,
                                        );
                                    },
                                    () =>
                                        h.div(
                                            {
                                                class: "py-3 text-muted d-flex align-items-center gap-3",
                                            },
                                            h.i({
                                                class: "fas fa-truck fs-3 text-secondary animate-pulse",
                                            }),
                                            h.p(
                                                {
                                                    class: "m-0 small fw-medium text-start",
                                                },
                                                "We are assigning a delivery unit to process your order shortly.",
                                            ),
                                        ),
                                ),
                            ),
                        ),
                    ),

                    // Delivery Address
                    either(
                        address,
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4" },
                            h.div(
                                { class: "card-body p-4" },
                                h.h5(
                                    {
                                        class: "fw-bold text-secondary mb-3 d-flex align-items-center gap-2",
                                    },
                                    h.i({ class: "bi bi-geo-alt" }),
                                    "Delivery Address",
                                ),
                                either(
                                    address?.address,
                                    h.h6(
                                        { class: "fw-bold mb-2" },
                                        h.a(
                                            {
                                                href: `https://maps.google.com/?q=${address?.latitude ?? 0},${address?.longitude ?? 0}`,
                                                target: "_blank",
                                                class: "text-decoration-none text-dark hover-primary d-flex align-items-start gap-2",
                                            },
                                            h.i({
                                                class: "bi bi-geo-alt text-primary mt-1",
                                            }),
                                            h.span(address?.address),
                                        ),
                                    ),
                                ),
                                h.div(
                                    { class: "d-flex flex-column gap-1" },

                                    either(
                                        address?.recipient_name ||
                                            address?.first_name ||
                                            address?.last_name,
                                        () =>
                                            h.p(
                                                {
                                                    class: "text-muted mb-0 small",
                                                },
                                                h.i({
                                                    class: "bi bi-person me-2",
                                                }),
                                                `${address?.recipient_name ?? `${address?.first_name ?? ""} ${address?.last_name ?? ""}`.trim()}`,
                                            ),
                                    ),

                                    either(address?.phone, () =>
                                        h.p(
                                            { class: "text-muted mb-0 small" },
                                            h.i({
                                                class: "bi bi-telephone me-2",
                                            }),
                                            address?.phone,
                                        ),
                                    ),

                                    either(
                                        address?.additional_information,
                                        () =>
                                            h.p(
                                                {
                                                    class: "mb-0 text-dark small",
                                                },
                                                h.i({
                                                    class: "bi bi-info-circle me-2 text-secondary",
                                                }),
                                                address?.additional_information,
                                            ),
                                    ),
                                ),
                            ),
                        ),
                    ),

                    either(val?.notes, () =>
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4" },
                            h.div(
                                { class: "card-body p-4" },
                                h.h5(
                                    {
                                        class: "fw-bold text-secondary mb-2 d-flex align-items-center gap-2",
                                    },
                                    h.i({ class: "bi bi-chat-right-text" }),
                                    "Order Notes",
                                ),
                                h.p({ class: "mb-0" }, val?.notes),
                            ),
                        ),
                    ),

                    // Order Items / Details
                    either(
                        val?.type == "delivery_request" || val?.items,
                        () => {
                            return h.section(
                                h.h5(
                                    {
                                        class: "fw-bold text-secondary mb-3 pt-2",
                                    },
                                    either(
                                        val?.type == "errand",
                                        "Errand Request Details",
                                        either(
                                            val?.type == "delivery_request",
                                            "Package Delivery Details",
                                            "Items Purchased",
                                        ),
                                    ),
                                ),

                                either(
                                    val?.type == "errand",
                                    () => {
                                        let item = lastOf(val?.items);
                                        let p = item.product;

                                        return h.OrderDetailErrandView(
                                            p,
                                            item,
                                            val,
                                            {
                                                class: "bg-white rounded-4 shadow-sm p-4 mb-4 border-0",
                                            },
                                        );
                                    },
                                    () =>
                                        either(
                                            val?.type == "delivery_request",
                                            () =>
                                                h.OrderDetailDeliveryRequestView(
                                                    val,
                                                ),
                                            () =>
                                                h.OrderedItems(val?.items, val),
                                        ),
                                ),
                            );
                        },
                    ),

                    // Payments Made
                    either(val?.payments && val?.payments?.length, () =>
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4" },
                            h.div(
                                { class: "card-body p-4" },
                                h.h5(
                                    { class: "fw-bold text-secondary mb-4" },
                                    "Payments Made",
                                ),
                                h.div(
                                    { class: "d-flex flex-column gap-3" },
                                    each(val?.payments, (payment) =>
                                        h.div(
                                            {
                                                class: "bg-light rounded-3 p-3 border border-light-subtle",
                                            },
                                            h.div(
                                                {
                                                    class: "d-flex justify-content-between align-items-start mb-3",
                                                },
                                                h.div(
                                                    h.h6(
                                                        {
                                                            class: "fw-bold mb-1",
                                                        },
                                                        monetary(
                                                            payment.amount,
                                                            payment.currency,
                                                        ),
                                                    ),
                                                    h.small(
                                                        { class: "text-muted" },
                                                        `via ${payment.gateway}`,
                                                    ),
                                                ),
                                                h.PaymentStatus(payment.status),
                                            ),
                                            h.div(
                                                {
                                                    class: "text-muted small mb-3",
                                                },
                                                h.div(
                                                    formatDateForHumans(
                                                        payment.created_at,
                                                    ),
                                                ),
                                                either(
                                                    payment.transaction_id,
                                                    h.div(
                                                        `ID: ${payment.transaction_id}`,
                                                    ),
                                                ),
                                            ),
                                            either(
                                                payment.status in
                                                    {
                                                        completed: true,
                                                        refund_pending: true,
                                                    } &&
                                                    val?.status == "cancelled",
                                                () =>
                                                    h.button(
                                                        {
                                                            class: "btn btn-outline-warning btn-sm rounded-pill px-3",
                                                            onclick:
                                                                this.method(
                                                                    "refundPayment",
                                                                    [
                                                                        val?.id,
                                                                        payment?.id,
                                                                    ],
                                                                ),
                                                        },
                                                        "Request Refund",
                                                    ),
                                            ),
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),

                    // Payment Requests
                    either(
                        val?.payment_requests &&
                            val?.payment_requests?.length > 0,
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4" },
                            h.div(
                                { class: "card-body p-4" },
                                h.div(
                                    {
                                        class: "d-flex justify-content-between align-items-center mb-4",
                                    },
                                    h.h5(
                                        {
                                            class: "fw-bold text-secondary mb-0",
                                        },
                                        "Payment Requests",
                                    ),
                                    either(
                                        uc?.settings?.value?.auto_price_top_up
                                            ?.value != 1,
                                        h.a(
                                            {
                                                href: "#",
                                                class: "small text-decoration-none",
                                                onclick: `route.to('/settings')`,
                                            },
                                            "Auto-pay settings",
                                        ),
                                    ),
                                ),
                                h.div(
                                    { class: "d-flex flex-column gap-3" },
                                    each(val?.payment_requests, (request) =>
                                        h.div(
                                            {
                                                class: "bg-light rounded-3 p-3 border border-light-subtle",
                                            },
                                            h.div(
                                                {
                                                    class: "d-flex justify-content-between align-items-start mb-2",
                                                },
                                                h.h6(
                                                    { class: "fw-bold mb-0" },
                                                    monetary(
                                                        request.amount,
                                                        request.currency,
                                                    ),
                                                ),
                                                h.PaymentStatus(request.status),
                                            ),
                                            h.div(
                                                {
                                                    class: "text-muted small mb-2",
                                                },
                                                h.span(
                                                    formatDateForHumans(
                                                        request.created_at,
                                                    ),
                                                ),
                                                h.span(" • "),
                                                h.span(
                                                    request.payment?.gateway ??
                                                        "Unpaid",
                                                ),
                                                either(
                                                    request.payment
                                                        ?.transaction_id,
                                                    () =>
                                                        h.span(
                                                            ` • ID: ${request.payment.transaction_id}`,
                                                        ),
                                                ),
                                            ),
                                            this.getRequestButtons(request),
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),

                    // Footer Actions
                    h.div(
                        {
                            class: "d-flex justify-content-between gap-3 pt-3",
                        },

                        h.BackButton(),

                        h.div(
                            { class: "d-flex gap-3" },
                            either(
                                val?.status == "confirmed" ||
                                    val?.status == "dispute" ||
                                    val?.status == "scheduled" ||
                                    (val?.type == "delivery_request" &&
                                        val?.status == "ready"),
                                h.button(
                                    {
                                        class: "btn btn-danger btn-icon-text rounded-pill px-4",
                                        onclick: this.method(
                                            "cancelOrder",
                                            val,
                                        ),
                                    },
                                    h.i({
                                        class: "fa-solid fa-triangle-exclamation me-2",
                                    }),
                                    "Cancel Order",
                                ),
                            ),
                            either(
                                val?.status == "pending",
                                h.button(
                                    {
                                        class: "btn btn-outline-danger btn-icon-text rounded-pill px-4",
                                        onclick: this.method(
                                            "deleteOrder",
                                            val,
                                        ),
                                    },
                                    h.i({ class: "fa-solid fa-trash me-2" }),
                                    "Delete Order",
                                ),
                            ),
                        ),
                    ),
                );
            }),
            ...args,
        );
    }

    infoRow(label, value, icon) {
        return h.div(
            { class: "d-flex justify-content-between align-items-center" },
            h.div(
                { class: "d-flex align-items-center gap-2 text-muted" },
                h.i({ class: icon }),
                h.span(label),
            ),
            h.span({ class: "fw-semibold text-dark" }, value),
        );
    }

    showOrderQR(order) {
        broker.send(
            $e.qr.needs.code,
            payload({
                title: `Order (${order?.order_code})`,
                data: `/orders/${order?.id}/track`,
            }),
        );
    }

    cancelOrder(order) {
        broker.broadcast(
            $e.order.needs.cancellation,
            payload({ id: order?.id }),
        );
    }

    deleteOrder(order) {
        broker.broadcast($e.order.needs.deletion, payload({ id: order?.id }));
    }

    getPayment(order) {
        broker.send(
            $e.order.needs.payment,
            payload({
                orderId: order?.id,
                amount: order?.unpaid,
                editable: true,
            }),
        );
    }

    refundPayment(orderId, paymentId) {
        broker.send(
            $e.order.needs.paymentRefund,
            payload({ orderId, paymentId }),
        );
    }

    getRequestButtons(request) {
        return !request.paid
            ? h.div(
                  { class: "mt-2" },
                  h.button(
                      {
                          class: "btn btn-sm btn-primary rounded-pill px-3 shadow-sm",
                          onclick: this.method("getRequestPayment", request),
                      },
                      "Pay now",
                  ),
              )
            : "";
    }

    renderAvatar(user, size = 3) {
        let photo = user?.profile_photo_path;
        if (photo) {
            return h.img({
                src: photo,
                class: "rounded-circle flex-shrink-0 object-fit-cover border cursor-pointer",
                style: `width: ${size}rem; height: ${size}rem; cursor: pointer;`,
                alt: `${user.first_name} ${user.last_name}`,
                // Tap the photo to see it larger, reusing the same
                // image-viewer modal used for product photos.
                onclick: this.method("seeImages", [[{ path: photo }]]),
            });
        }
        return h.i({
            class: "fas fa-user-circle text-secondary-subtle flex-shrink-0",
            style: `font-size: ${size}rem;`,
        });
    }

    // A single Purchase Coordinator / Logistics Associate card, meant to
    // sit in a two-column row (see the "Delivery Unit" section) - so it's
    // laid out vertically/centered rather than the old left-avatar,
    // right-buttons row, which doesn't fit a half-width column well.
    renderUnitPersonCard(person, roleLabel, val) {
        // First name only - keeps this short enough to never wrap or need
        // an ellipsis inside a half-width column.
        let name = person?.first_name || "";
        let rawPhone = person?.phone;
        let phone =
            rawPhone && rawPhone[0] != "+" ? "+" + rawPhone : rawPhone;

        return h.div(
            {
                class: "border border-light-subtle rounded-4 p-3 bg-light-subtle shadow-sm h-100 d-flex flex-column align-items-center text-center",
            },
            this.renderAvatar(person, 4.5),
            h.h6(
                { class: "fw-bold text-dark mb-1 mt-2 text-truncate w-100" },
                name,
            ),
            h.span(
                {
                    class: "text-secondary small fw-bold text-uppercase mb-3",
                    style: "font-size: 0.65rem; letter-spacing: 0.5px;",
                },
                roleLabel,
            ),
            h.div(
                { class: "d-flex gap-2 mt-auto" },
                h.a(
                    {
                        class: "btn btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center",
                        style: "width: 38px; height: 38px;",
                        href: `tel:${phone}`,
                    },
                    h.i({ class: "fas fa-phone" }),
                ),
                h.a(
                    {
                        class: "btn btn-outline-success rounded-circle p-2 d-flex align-items-center justify-content-center",
                        style: "width: 38px; height: 38px;",
                        href: `https://api.whatsapp.com/send?phone=${phone}&text=Hi, I am reaching out regarding order ${val?.order_code}`,
                    },
                    h.i({ class: "fa-brands fa-whatsapp" }),
                ),
            ),
        );
    }

    getSurplusButtons(surplus) {
        if (surplus.reimbursed || surplus.payment_id) return;

        return surplus.type == "negative"
            ? h.button(
                  {
                      class: "btn btn-sm btn-success rounded-pill px-3 py-2 w-100",
                      onclick: this.method("paySurplus", surplus),
                  },
                  "Refund Carata",
              )
            : h.div(
                  h.button(
                      {
                          class: "btn btn-sm btn-success rounded-pill px-3 py-2 w-100 mb-2",
                          onclick: this.method("claimSurplus", surplus),
                      },
                      `Reimburse Me`,
                  ),
                  h.small(
                      { class: "text-muted d-block text-center" },
                      "Refunds sent to last payment billing address.",
                  ),
              );
    }

    claimSurplus(surplus) {
        broker.send(
            $e.surplus.needs.claim,
            payload({ surplusId: surplus.id, orderId: surplus.order_id }),
        );
    }

    paySurplus(surplus) {
        broker.send(
            $e.surplus.needs.negativeReimbursement,
            payload(
                {
                    surplusId: surplus.id,
                    orderId: surplus.order_id,
                    amount: surplus.amount,
                    surplus,
                },
                { next: route.current() },
            ),
        );
    }

    getRequestPayment(request) {
        broker.send(
            $e.paymentRequest.needs.payment,
            payload({ request }, { next: route.current() }),
        );
    }

    refresh(orderId) {
        this.fetchOrder();
    }

    editReview(orderId, review) {
        broker.send($e.review.needs.creation, payload({ orderId, review }));
    }

    showDescription(elem, productId) {
        let description = dom.get(`[hook="toc-${productId}-description"]`);
        let text = "show description";

        if (description.classList.contains("d-none")) {
            text = "hide description";
        }

        dom.put(text, elem);
        description.classList.toggle("d-none");
    }

    seeImages(images) {
        if (this.imagesModal) {
            cleanDispose(this.imagesModal);
        }

        this.imagesModal = tool.plainModal(
            h.div(
                {
                    class: "modal-content border-0 w-auto mx-auto bg-transparent shadow-none",
                },
                h.div({ class: "modal-body p-0" }, h.ProductImageView(images)),
            ),
            {},
        );

        this.imagesModal.show();
    }

    $$review = {
        stored: (ed, event) => {
            let { message } = eParse(ed);
            this.review.value = message.get("review", null);

            // Give the review modal a moment to close before possibly
            // nudging the customer to rate the app.
            setTimeout(() => maybeTriggerAppReviewPrompt(), 500);
        },
    };

    $$order = {
        cancelled: (ed, event) => {
            let { message } = eParse(ed);

            if (message.orderId == this.orderTracked.value?.id) {
                this.fetchOrder();
            }
        },
    };

    $$echo = {
        OrderCancelled_OrderStatusChanged_OrderConfirmed_OrderUpdated: (
            ed,
            event,
        ) => {
            if (!route.is("order.track")) return;

            const { message } = eParse(ed);

            let data = message.data;

            if (data.id == route.params.orderId) {
                this.fetchOrder();
            }
        },
    };

    cleanUp() {
        super.cleanUp();
        this.review.value = null;
        this.orderTracked.value = null;
        clearInterval(this.timeoutId);
        clearTimeout(this.reviewTimeoutId);
        clearTimeout(this.businessRatingTimeoutId);
        cleanDispose(this.imagesModal);
        if (route.params.orderId) {
            leaveEchoChannel(`orders.${route.params.orderId}`);
        }
    }
}
